import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTenants } from 'app/entities/tenant/tenant.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { getEntities as getContactDetails } from 'app/entities/contact-details/contact-details.reducer';
import { OrderStatus } from 'app/shared/model/enumerations/order-status.model';
import { EntityType } from 'app/shared/model/enumerations/entity-type.model';
import { createEntity, getEntity, reset, updateEntity } from './tinymedic-patient-order.reducer';

export const TinymedicPatientOrderUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tenants = useAppSelector(state => state.tenant.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const contactDetails = useAppSelector(state => state.contactDetails.entities);
  const tinymedicPatientOrderEntity = useAppSelector(state => state.tinymedicPatientOrder.entity);
  const loading = useAppSelector(state => state.tinymedicPatientOrder.loading);
  const updating = useAppSelector(state => state.tinymedicPatientOrder.updating);
  const updateSuccess = useAppSelector(state => state.tinymedicPatientOrder.updateSuccess);
  const orderStatusValues = Object.keys(OrderStatus);
  const entityTypeValues = Object.keys(EntityType);

  const handleClose = () => {
    navigate(`/tinymedic-patient-order${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTenants({}));
    dispatch(getGlucoUsers({}));
    dispatch(getContactDetails({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...tinymedicPatientOrderEntity,
      ...values,
      tenant: tenants.find(it => it.id.toString() === values.tenant?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
      deliveryAddress: contactDetails.find(it => it.id.toString() === values.deliveryAddress?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          status: 'CREATED',
          orderType: 'PHARMACY',
          ...tinymedicPatientOrderEntity,
          tenant: tinymedicPatientOrderEntity?.tenant?.id,
          patient: tinymedicPatientOrderEntity?.patient?.id,
          deliveryAddress: tinymedicPatientOrderEntity?.deliveryAddress?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.tinymedicPatientOrder.home.createOrEditLabel" data-cy="TinymedicPatientOrderCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.tinymedicPatientOrder.home.createOrEditLabel">
              Create or edit a TinymedicPatientOrder
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="tinymedic-patient-order-id"
                  label={translate('tinyMedicRestApp.tinymedicPatientOrder.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.status')}
                id="tinymedic-patient-order-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {orderStatusValues.map(orderStatus => (
                  <option value={orderStatus} key={orderStatus}>
                    {translate(`tinyMedicRestApp.OrderStatus.${orderStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.orderType')}
                id="tinymedic-patient-order-orderType"
                name="orderType"
                data-cy="orderType"
                type="select"
              >
                {entityTypeValues.map(entityType => (
                  <option value={entityType} key={entityType}>
                    {translate(`tinyMedicRestApp.EntityType.${entityType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.createdById')}
                id="tinymedic-patient-order-createdById"
                name="createdById"
                data-cy="createdById"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.active')}
                id="tinymedic-patient-order-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.cursor')}
                id="tinymedic-patient-order-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.lastUpdated')}
                id="tinymedic-patient-order-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.microSec')}
                id="tinymedic-patient-order-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.timestampEntry')}
                id="tinymedic-patient-order-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="tinymedic-patient-order-tenant"
                name="tenant"
                data-cy="tenant"
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.tenant')}
                type="select"
              >
                <option value="" key="0" />
                {tenants
                  ? tenants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="tinymedic-patient-order-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.patient')}
                type="select"
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="tinymedic-patient-order-deliveryAddress"
                name="deliveryAddress"
                data-cy="deliveryAddress"
                label={translate('tinyMedicRestApp.tinymedicPatientOrder.deliveryAddress')}
                type="select"
              >
                <option value="" key="0" />
                {contactDetails
                  ? contactDetails.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/tinymedic-patient-order" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TinymedicPatientOrderUpdate;
